import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { Loading } from "app/components/Wrappers";
import { cls } from "app/utils";
import LeftArrow from "app/images/back-icon.svg";
import styles from "./styles.module.scss";
import FacilityList from "./screens/FacilityList";
import CoachList from "./screens/CoachList";
import FacilityOfferings from "./screens/FacilityOfferings";
import CoachPackages from "./screens/CoachPackages";

type Screen =
  | "facilityList"
  | "facilityOfferings"
  | "coachList"
  | "coachPackages";

interface Props {
  onClose: () => void;
}

const FacilityOptionsModal = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const self = useSelector((state: RootState) => state.user.self);

  const [screen, setScreen] = useState<Screen>("facilityList");
  const [loading, setLoading] = useState(true);
  const [multipleFacilities, setMultipleFacilities] = useState(false);
  const [facilities, setFacilities] = useState<any[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<any>(null);
  const [coaches, setCoaches] = useState<any[]>([]);
  const [selectedCoach, setSelectedCoach] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    schedulingFetchGet("facilities", {}, {}, controller.signal)
      .then((data) => {
        const list = Array.isArray(data) ? data : [data];
        setFacilities(list);
        setMultipleFacilities(list.length > 1);

        if (list.length === 1) {
          selectFacility(list[0]);
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error("[PurchaseOptions] Failed to fetch facilities:", err);
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, []);

  const selectFacility = (facility: any) => {
    setSelectedFacility(facility);
    setLoading(true);

    const isFacilityLevel =
      facility?.featureFlags?.facilityLevelBookingOptions === true;

    if (isFacilityLevel) {
      setScreen("facilityOfferings");
      schedulingFetchGet(
        "stripe_student_products",
        { facility_id: facility.id },
        {},
      )
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          const filtered = list.filter(
            (p) => p.enabled === true || p.subscribed === true,
          );
          setProducts(filtered);
        })
        .catch((err) =>
          console.error(
            "[PurchaseOptions] Failed to fetch facility offerings:",
            err,
          ),
        )
        .finally(() => setLoading(false));
    } else {
      setScreen("coachList");
      schedulingFetchGet("coach_profiles", { facility_id: facility.id }, {})
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          // CN-2805: the endpoint ignores facility_id and answers with every
          // coach the athlete can book across all their facilities, so an athlete
          // at several academies saw the same full list whichever facility they
          // picked. Filter here; each coach carries the facility they belong to.
          setCoaches(
            list.filter((c) => String(c.facilityId) === String(facility.id)),
          );
        })
        .catch((err) =>
          console.error("[PurchaseOptions] Failed to fetch coaches:", err),
        )
        .finally(() => setLoading(false));
    }
  };

  const selectCoach = (coach: any) => {
    setSelectedCoach(coach);
    setScreen("coachPackages");
    setLoading(true);

    schedulingFetchGet("stripe_student_products", { coach_id: coach.id }, {})
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const filtered = list.filter(
          (p) => p.enabled === true || p.subscribed === true,
        );
        setProducts(filtered);
      })
      .catch((err) =>
        console.error("[PurchaseOptions] Failed to fetch coach packages:", err),
      )
      .finally(() => setLoading(false));
  };

  const handleBack = () => {
    if (screen === "coachPackages") {
      setScreen("coachList");
    } else if (screen === "coachList" || screen === "facilityOfferings") {
      if (multipleFacilities) {
        setScreen("facilityList");
      } else {
        onClose();
      }
    }
  };

  const title = () => {
    switch (screen) {
      case "facilityList":
        return t("Select a Facility");
      case "facilityOfferings":
        return selectedFacility?.name || t("Offerings");
      case "coachList":
        return selectedFacility?.name || t("Select a Coach");
      case "coachPackages":
        return `${selectedCoach?.firstName ?? ""} ${selectedCoach?.lastName ?? ""}`.trim();
    }
  };

  return (
    <div className={styles.modal_wrapper}>
      <div className={styles.modal_container}>
        <div className={styles.modal_header}>
          <div className={styles.header_left}>
            {screen !== "facilityList" && (
              <img
                className={styles.back_icon}
                src={LeftArrow}
                alt="Back"
                onClick={handleBack}
              />
            )}
            <h3>{title()}</h3>
          </div>
        </div>
        <i className={cls("ico ico-x", styles.close_icon)} onClick={onClose} />
        {loading ? (
          <Loading isLoading loadType="spinner" />
        ) : (
          <>
            {screen === "facilityList" && (
              <FacilityList facilities={facilities} onSelect={selectFacility} />
            )}
            {screen === "coachList" && (
              <CoachList coaches={coaches} onSelect={selectCoach} />
            )}
            {screen === "facilityOfferings" && (
              <FacilityOfferings
                products={products}
                studentId={self?.id}
              />
            )}
            {screen === "coachPackages" && (
              <CoachPackages
                products={products}
                coachId={selectedCoach?.id ?? null}
                studentId={self?.id}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FacilityOptionsModal;
